// ============================================
// Path: /frontend/src/pages/ListingDetailPage.jsx
// Purpose: Display detailed view of a single listing with inquiry functionality
// FIXED: Carousel, watermark, buttons, contact display
// ============================================

// ========================================
// IMPORTS
// ========================================
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.jsx';
import { useToast } from '../hooks/useToast';
import { listingsAPI } from '../api/listings.api';
import { inquiriesAPI } from '../api/inquiries.api';
import { whatsappAPI } from '../api/whatsapp.api';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import ListingImage from '../components/ListingImage';
import { InquiryModal } from '../components/InquiryModal';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import ContactSafetyModal from '../components/modals/ContactSafetyModal';
import { Loader2, MapPin, Tag, Car, Home, User, Mail, Phone, Calendar, BadgeAlert, ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import ShareOnWhatsAppButton from '../components/whatsapp/ShareOnWhatsAppButton';
import ChatOnWhatsAppButton from '../components/whatsapp/ChatOnWhatsAppButton';
import SaveListingButton from '../components/listings/SaveListingButton';
import CompareButton from '../components/listings/CompareButton';
import PriceAlertButton from '../components/listings/PriceAlertButton';
import SharesSelector from '../components/listings/SharesSelector'; // NEW NAME
import SharesInterestForm from '../components/listings/SharesInterestForm'; // NEW NAME
import SharesProgressBoard from '../components/listings/SharesProgressBoard'; // NEW NAME
import SimilarListings from '../components/listings/SimilarListings';
import RecentlyViewed from '../components/listings/RecentlyViewed';
import ChamaHowItWorks from '../components/listings/ChamaHowItWorks';

// ========================================
// IMAGE CAROUSEL COMPONENT
// ========================================
const ImageCarousel = ({ images, title, category, posterName, appName = "YourApp" }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Handle case where images might be a single string or array
  const imageArray = Array.isArray(images) ? images : [images];
  const totalImages = imageArray.length;

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? totalImages - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === totalImages - 1 ? 0 : prev + 1));
  };

  const goToImage = (index) => {
    setCurrentIndex(index);
  };

  return (
    <div className="relative aspect-video w-full rounded-lg overflow-hidden mb-8 bg-black">
      {/* Main Image with Watermark */}
      <div className="relative w-full h-full">
        <ListingImage
          src={imageArray[currentIndex]}
          alt={`${title} - Image ${currentIndex + 1}`}
          type={category?.toLowerCase()}
          className="w-full h-full object-cover"
        />
        
        {/* Watermark Overlay */}
        <div className="absolute bottom-3 right-3 text-white/40 text-xs font-medium pointer-events-none">
          {posterName}
        </div>
      </div>

      {/* Navigation Arrows - Only show if more than 1 image */}
      {totalImages > 1 && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-all"
            aria-label="Previous image"
          >
            <ChevronLeft className="w-5 h-5 text-gray-800" />
          </button>
          
          <button
            onClick={goToNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-all"
            aria-label="Next image"
          >
            <ChevronRight className="w-5 h-5 text-gray-800" />
          </button>
        </>
      )}

      {/* Image Counter */}
      {totalImages > 1 && (
        <div className="absolute top-3 right-3 bg-black/70 backdrop-blur-sm px-3 py-1 rounded-full text-white text-xs font-semibold">
          {currentIndex + 1} / {totalImages}
        </div>
      )}

      {/* Thumbnail Indicators */}
      {totalImages > 1 && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
          {imageArray.map((_, index) => (
            <button
              key={index}
              onClick={() => goToImage(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentIndex 
                  ? 'bg-white w-6' 
                  : 'bg-white/50 hover:bg-white/75'
              }`}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ========================================
// LOADING SKELETON COMPONENT
// ========================================
const LoadingSkeleton = () => (
  <div className="flex-grow container py-6 sm:py-8 md:py-12 px-4">
    <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
      <div>
        <Skeleton className="aspect-video w-full rounded-lg" />
      </div>
      <div className="space-y-6">
        <Skeleton className="h-10 w-3/4" />
        <Skeleton className="h-8 w-1/4" />
        <div className="space-y-3">
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-5/6" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
        </div>
      </div>
    </div>
  </div>
);

// ========================================
// STATUS ALERT COMPONENT
// ========================================
const StatusAlert = ({ status }) => {
  let variant = 'default';
  let title = '';
  let description = '';

  switch (status) {
    case 'PENDING':
      title = 'Listing Pending Review';
      description = 'This listing is not yet visible to the public. It is awaiting approval from an administrator.';
      break;
    case 'REJECTED':
      variant = 'destructive';
      title = 'Listing Rejected';
      description = 'This listing was rejected by an administrator and is not visible to the public.';
      break;
    default:
      return null;
  }

  return (
    <Alert variant={variant} className="mb-4 sm:mb-6 md:mb-8">
      <BadgeAlert className="h-4 w-4" />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>{description}</AlertDescription>
    </Alert>
  );
};

// ========================================
// MAIN COMPONENT
// ========================================
export default function ListingDetailPage() {
  console.log('🎨 [ListingDetailPage] Component mounted');

  // ========================================
  // HOOKS & STATE
  // ========================================
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();

  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInquiryModalOpen, setIsInquiryModalOpen] = useState(false);
  const [showListingDetails, setShowListingDetails] = useState(false);
  const [showDescription, setShowDescription] = useState(false);
  const [hasInquired, setHasInquired] = useState(false); // NEW: Track if user has inquired
  const [showContactModal, setShowContactModal] = useState(false); // For public users
  const [showContactForPublic, setShowContactForPublic] = useState(false); // Phone revealed
  const [selectedSharesData, setSelectedSharesData] = useState(null);
  const [showSharesForm, setShowSharesForm] = useState(false);

  
  // ========================================
  // DATA FETCHING
  // ========================================
  useEffect(() => {
    const fetchListing = async () => {
      console.log('🔵 [ListingDetailPage] Fetching listing...', { id });
      setLoading(true);

      try {
        const data = await listingsAPI.getListing(id);
        console.log('✅ [ListingDetailPage] Listing received:', data);
        setListing(data.listing);
        setError(null);
      } catch (err) {
        console.error('❌ [ListingDetailPage] Fetch error:', err);
        setError(err.response?.data?.message || 'Failed to load listing');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchListing();
    }
  }, [id]);

  // ========================================
  // WHATSAPP CLICK TRACKING
  // ========================================
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const trackingId = urlParams.get('tid');
    const source = urlParams.get('src');

    if (trackingId && source === 'whatsapp') {
      const trackedKey = `whatsapp_tracked_${trackingId}`;
      
      if (!sessionStorage.getItem(trackedKey)) {
        console.log('[ListingDetailPage] 📊 Tracking WhatsApp click:', trackingId);
        
        whatsappAPI.trackClick(trackingId)
          .then(() => {
            sessionStorage.setItem(trackedKey, 'true');
            console.log('[ListingDetailPage] ✅ Click tracked successfully');
          })
          .catch(error => {
            console.error('[ListingDetailPage] ❌ Failed to track click:', error);
          });
      } else {
        console.log('[ListingDetailPage] ⏭️ Click already tracked in this session');
      }
    }
  }, []);

    // Track recently viewed listings
    useEffect(() => {
      if (listing && listing.id) {
        const recentlyViewed = JSON.parse(localStorage.getItem('recentlyViewed') || '[]');
        const filtered = recentlyViewed.filter(id => id !== listing.id);
        const updated = [listing.id, ...filtered].slice(0, 10);
        localStorage.setItem('recentlyViewed', JSON.stringify(updated));
      }
    }, [listing]);

  // ========================================
  // EVENT HANDLERS
  // ========================================
  const handleOpenInquiryModal = () => {
    console.log('👤 [ListingDetailPage] User clicked inquiry/contact button');

    // PUBLIC USER - Show contact safety modal
    if (!user) {
      console.log('⚠️ [ListingDetailPage] Public user - showing contact modal');
      setShowContactModal(true);
      return;
    }

    // AUTHENTICATED USER - Show inquiry modal
    console.log('✅ [ListingDetailPage] Authenticated user - showing inquiry modal');
    setIsInquiryModalOpen(true);
  };

  const handleRevealContact = () => {
    console.log('📞 [ListingDetailPage] Revealing contact for public user');
    setShowContactForPublic(true);
  };

  const handleSubmitInquiry = async (message) => {
    if (!listing) return;

    console.log('🔵 [ListingDetailPage] Creating inquiry...', {
      listingId: listing.id,
      listingTitle: listing.title,
      message,
    });

    setIsSubmitting(true);

    try {
      const inquiryData = {
        listingId: listing.id,
        message: message || undefined,
      };

      const response = await inquiriesAPI.createInquiry(inquiryData);
      console.log('✅ [ListingDetailPage] Inquiry created:', response);

      toast({
        title: 'Inquiry Sent!',
        description: 'Contact information is now visible below.',
      });

      setHasInquired(true); // NEW: Mark as inquired
      setIsInquiryModalOpen(false);
    } catch (err) {
      console.error('❌ [ListingDetailPage] Inquiry error:', err);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err.response?.data?.message || 'Could not send your inquiry. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ========================================
  // HELPER FUNCTIONS
  // ========================================
  const renderDetail = (Icon, label, value) => {
    if (!value) return null;
    return (
      <div className="flex items-center gap-3 text-sm">
        <Icon className="w-5 h-5 text-muted-foreground flex-shrink-0" />
        <div>
          <p className="font-semibold">{label}</p>
          <p className="text-muted-foreground">{value}</p>
        </div>
      </div>
    );
  };

  const canViewListing =
    listing &&
    (listing.status === 'APPROVED' ||
      user?.role === 'ADMIN' ||
      user?.role === 'MODERATOR');

  // ========================================
  // LOADING STATE
  // ========================================
  if (loading || authLoading) {
    console.log('⏳ [ListingDetailPage] Loading...');
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow">
          <LoadingSkeleton />
        </main>
        <Footer />
      </div>
    );
  }

  // ========================================
  // ERROR STATE
  // ========================================
  if (error) {
    console.log('🔴 [ListingDetailPage] Error state:', error);
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow">
          <div className="text-center text-muted-foreground py-8 sm:py-12 text-sm sm:text-base px-4">
            Error: {error}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // ========================================
  // NO ACCESS STATE
  // ========================================
  if (!canViewListing) {
    console.log('⚠️ [ListingDetailPage] User cannot view listing');
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow">
          <div className="text-center text-muted-foreground py-8 sm:py-12 text-sm sm:text-base px-4">
            Listing not found or you do not have permission to view it.
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // ========================================
  // RENDER
  // ========================================
  console.log('🎨 [ListingDetailPage] Rendering listing:', listing);

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow">
        <div className="flex-grow container py-6 sm:py-8 md:py-12 px-4">
          {listing.status !== 'APPROVED' && <StatusAlert status={listing.status} />}

          <div className="grid md:grid-cols-5 gap-3 md:gap-12">
            <div className="md:col-span-3">
              {/* CAROUSEL COMPONENT */}
              <ImageCarousel
                images={listing.imageUrl}
                title={listing.title}
                category={listing.category}
                posterName={listing.user?.name || 'Seller'}
                appName="ListHub"
              />

              <div className="text-left mb-3">
                <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold mb-3">
                  {listing.title}
                </h1>
                <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-primary">
                  KES {listing.price.toLocaleString()}
                </p>
              </div>

              {/* ACTION BUTTONS ROW */}
              <div className="flex flex-wrap gap-2 mb-4">
                <SaveListingButton listingId={listing.id} />
                <CompareButton listing={listing} />
                <PriceAlertButton listingId={listing.id} />
              </div>

              {/* CHAMA/SHARES SECTION - Only show if listing is chama-eligible */}
             {listing.allowGroupBuy && (
                <Card className="mb-4 bg-muted/40">
                  <CardHeader className="p-3 sm:p-4">
                    <h2 className="text-base sm:text-lg font-semibold">Buy with Shares</h2>
                  </CardHeader>
                  <CardContent className="p-3 sm:p-4 pt-0 space-y-4">
                    <SharesProgressBoard 
                      listingId={listing.id} 
                      onBuyShares={() => {
                        const sharesSection = document.getElementById('shares-selector');
                        if (sharesSection) {
                          sharesSection.scrollIntoView({ behavior: 'smooth' });
                        }
                      }}
                    />
                    <div id="shares-selector">
                      <SharesSelector 
                        listingId={listing.id}
                        onPurchaseClick={(sharesData) => {
                          setSelectedSharesData(sharesData);
                          setShowSharesForm(true);
                        }}
                      />
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* CHAMA HOW IT WORKS - For Buyers */}
              {listing.allowGroupBuy && (
                <ChamaHowItWorks audience="buyer" />
              )}

              {/* COLLAPSIBLE DESCRIPTION */}
              <section className="bg-muted/40 rounded-lg p-3 sm:p-4 md:p-6 mb-1.5">
                <button
                  onClick={() => setShowDescription(!showDescription)}
                  className="w-full flex items-center justify-between text-left transition-all duration-200 hover:opacity-80 mb-2 sm:mb-3"
                >
                  <h2 className="text-base sm:text-lg md:text-xl font-semibold text-foreground">
                    Description
                  </h2>
                  {showDescription ? (
                    <ChevronUp className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                  )}
                </button>
                {showDescription && (
                  <p className="text-xs sm:text-sm md:text-base text-foreground/80 leading-relaxed whitespace-pre-wrap">
                    {listing.description}
                  </p>
                )}
              </section>
            </div>

            <div className="md:col-span-2 space-y-3">
              {/* COLLAPSIBLE LISTING DETAILS */}
              <Card className="bg-muted/40">
                <CardHeader className="p-3 sm:p-4 md:p-6 pb-2 sm:pb-3">
                  <button
                    onClick={() => setShowListingDetails(!showListingDetails)}
                    className="w-full flex items-center justify-between text-left transition-all duration-200 hover:opacity-80"
                  >
                    <h2 className="text-base sm:text-lg md:text-xl font-semibold text-foreground">
                      Listing Details
                    </h2>
                    {showListingDetails ? (
                      <ChevronUp className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                    )}
                  </button>
                </CardHeader>
                {showListingDetails && (
                  <CardContent className="space-y-4 p-3 sm:p-4 md:p-6 pt-0">
                    {renderDetail(
                      listing.category === 'VEHICLE' ? Car : Home,
                      'Category',
                      listing.category
                    )}
                    {renderDetail(Tag, 'Type', listing.type)}
                    {renderDetail(
                      MapPin,
                      'Location',
                      `${listing.subCounty}, ${listing.county}`
                    )}
                    {listing.make && renderDetail(Car, 'Make', listing.make)}
                    {listing.model && renderDetail(Car, 'Model', listing.model)}
                    {listing.createdAt &&
                      renderDetail(
                        Calendar,
                        'Posted On',
                        format(new Date(listing.createdAt), 'PPP')
                      )}
                  </CardContent>
                )}
              </Card>

              {/* SELLER INFO - FIXED BUTTONS */}
              <Card className="bg-muted/40">
                <CardHeader className="p-3 sm:p-4 md:p-6">
                  <h2 className="text-base sm:text-lg md:text-xl font-semibold text-foreground">
                    Contact Seller
                  </h2>
                </CardHeader>
                <CardContent className="space-y-4 p-3 sm:p-4 md:p-6 pt-0">

                 <div className="flex flex-col gap-3">
                    {/* Contact/Inquiry Button - Different for public vs authenticated */}
                    {(!user && showContactForPublic) ? (
                      // PUBLIC USER - After revealing contact
                      <div className="flex items-center gap-2 w-full">
                        <a
                          href={`tel:${listing.user?.phone}`}
                          className="flex-1 flex items-center justify-center gap-2 h-11 px-4 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-xs sm:text-sm font-medium"
                        >
                          📞 {listing.user?.phone || 'Not available'}
                        </a>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-11 w-11 flex-shrink-0"
                          onClick={() => {
                            navigator.clipboard.writeText(listing.user?.phone || '');
                            toast({
                              title: 'Copied!',
                              description: 'Phone number copied to clipboard',
                            });
                          }}
                          title="Copy phone number"
                        >
                          📋
                        </Button>
                      </div>
                    ) : (
                      // PUBLIC USER - Before revealing OR AUTHENTICATED USER
                      <Button
                        className="w-full text-xs sm:text-sm h-11 font-medium"
                        onClick={handleOpenInquiryModal}
                      >
                        {user ? 'Send Inquiry' : 'Contact Seller'}
                      </Button>
                    )}
                    
                    <ChatOnWhatsAppButton 
                      listingId={listing?.id}
                      listingTitle={listing?.title}
                      variant="full"
                      size="md"
                      className="w-full text-xs sm:text-sm h-11 font-medium bg-white hover:bg-gray-50 text-gray-900 border border-input"
                    />
                    <ShareOnWhatsAppButton 
                      listingId={listing?.id}
                      variant="button"
                      size="md"
                      className="w-full text-xs sm:text-sm h-11 font-medium bg-background hover:bg-accent text-foreground border border-input"
                    />
                  </div>
                  
                  {/* Contact info display - Different for public vs authenticated */}
                  <div className="pt-3 border-t border-border space-y-3">
                    {user ? (
                      <>
                        {/* AUTHENTICATED USER - Show after inquiry */}
                        {renderDetail(
                          User, 
                          'Contact Person', 
                          hasInquired ? (listing.user?.name || 'Seller') : 'Available after inquiry'
                        )}
                        {renderDetail(
                          Mail, 
                          'Email', 
                          hasInquired ? (listing.user?.email || 'Not provided') : 'Available after inquiry'
                        )}
                        {renderDetail(
                          Phone, 
                          'Phone', 
                          hasInquired ? (listing.user?.phone || 'Not provided') : 'Available after inquiry'
                        )}
                      </>
                    ) : (
                      <>
                        {/* PUBLIC USER - Show phone after contact modal, hide name/email */}
                        {renderDetail(
                          User, 
                          'Contact Person', 
                          'Seller'
                        )}
                        {showContactForPublic && renderDetail(
                          Phone, 
                          'Phone', 
                          listing.user?.phone || 'Not provided'
                        )}
                      </>
                    )}
                  </div>

                </CardContent>
              </Card>
            </div>
          </div>
          {/* ADD SIMILAR LISTINGS HERE */}
          <div className="mt-8">
            <SimilarListings 
              currentListingId={listing.id}
              category={listing.category}
              county={listing.county}
            />
          </div>

          {/* ADD RECENTLY VIEWED HERE */}
          <div className="mt-8">
            <RecentlyViewed currentListingId={listing.id} />
          </div>
        </div>
      </main>
      <Footer />

      {/* INQUIRY MODAL */}
      <InquiryModal
        isOpen={isInquiryModalOpen}
        onClose={() => setIsInquiryModalOpen(false)}
        onSubmit={handleSubmitInquiry}
        listingTitle={listing?.title}
        isSubmitting={isSubmitting}
      />

      {/* CONTACT SAFETY MODAL - For public users */}
      <ContactSafetyModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
        onProceed={handleRevealContact}
      />

      {/* SHARES INTEREST MODAL */}
      {showSharesForm && selectedSharesData && (
        <SharesInterestForm
          open={showSharesForm}
          onOpenChange={setShowSharesForm}
          listingId={listing.id}
          sharesData={selectedSharesData}
        />
      )}
    </div>
  );
}